'use client';

import { useState, useCallback } from 'react';
import { Search, X, Building2 } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { useSearchCompanyLazyQuery } from '@/gql/generated/graphql';
import { cn } from '@/lib/utils';

interface CompanyOption {
  dbId: number;
  shortName: string;
  uniqueDomain?: string | null;
  country?: string | null;
}

interface CompanySearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (company: CompanyOption) => void;
}

/**
 * Dialog for searching and selecting a company when creating a new AI Upload session.
 * Uses the existing cortexFirms GraphQL query.
 */
export function CompanySearchDialog({ open, onClose, onSelect }: CompanySearchDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchCompany, { data, loading }] = useSearchCompanyLazyQuery();

  const debouncedSearch = useDebouncedCallback((term: string) => {
    if (term.trim().length >= 2) {
      searchCompany({ variables: { searchTerm: term.trim(), first: 10 } });
    }
  }, 300);

  const handleInputChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleSelect = (company: CompanyOption) => {
    onSelect(company);
    setSearchTerm('');
    onClose();
  };

  if (!open) return null;

  const companies: CompanyOption[] =
    data?.cortexFirms?.edges
      ?.map(edge => edge?.node)
      .filter((node): node is CompanyOption => node !== null && node !== undefined) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-charcoal">Select a Company</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search Input */}
        <div className="px-4 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={e => handleInputChange(e.target.value)}
              placeholder="Search by company name or domain..."
              autoFocus
              className="w-full pl-9 pr-4 py-2.5 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20"
            />
          </div>
        </div>

        {/* Results */}
        <div className="max-h-64 overflow-y-auto px-2 pb-3">
          {loading && <div className="text-center text-gray-400 text-sm py-4">Searching...</div>}
          {!loading && searchTerm.length >= 2 && companies.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-4">No companies found</div>
          )}
          {!loading && searchTerm.length < 2 && (
            <div className="text-center text-gray-400 text-sm py-4">Type at least 2 characters to search</div>
          )}
          {companies.map(company => (
            <button
              key={company.dbId}
              onClick={() => handleSelect(company)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 text-left transition-colors"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-1/10 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-blue-1" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-charcoal truncate">{company.shortName}</p>
                <p className="text-xs text-gray-400 truncate">
                  {[company.uniqueDomain, company.country].filter(Boolean).join(' · ')}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
